import { useEffect, useState } from "react";
import ClientLayout from "../components/ClientLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function ClientTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(response.data);
    } catch (error) {
      toast.error("Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/tickets`,
        { subject, description, image_url: null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Ticket created successfully");
      setOpen(false);
      resetForm();
      fetchTickets();
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setSubject("");
    setDescription("");
  };

  const getStatusBadge = (status) => {
    const colors = {
      Open: "bg-blue-100 text-blue-700 border-blue-200",
      Ongoing: "bg-amber-100 text-amber-700 border-amber-200",
      Resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Closed: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Open}`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black text-slate-900">
              Support Tickets
            </h1>
            <p className="text-slate-600 mt-2">
              Create and track your support requests
            </p>
          </div>
          <Dialog
            open={open}
            onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-indigo-700 hover:bg-indigo-800 gap-2"
                data-testid="create-ticket-btn"
              >
                <Plus className="w-4 h-4" />
                New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="overflow-hidden">
              <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                <DialogTitle>Create Support Ticket</DialogTitle>
              </DialogHeader>
              <div className="px-6 pb-6">
                <form
                  onSubmit={handleCreate}
                  className="space-y-4"
                  data-testid="ticket-form"
                >
                  <div>
                    <Label>Subject</Label>
                    <Input
                      data-testid="ticket-subject-input"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Brief description of the issue"
                      required
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      data-testid="ticket-description-input"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Provide detailed information about your issue"
                      rows={6}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="w-full bg-indigo-700 hover:bg-indigo-800"
                    data-testid="ticket-submit-btn"
                  >
                    {creating ? "Creating..." : "Create Ticket"}
                  </Button>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {tickets.length === 0 ? (
            <div className="bg-white border border-slate-200 p-8 text-center text-slate-500">
              No tickets yet. Create your first support ticket!
            </div>
          ) : (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white border border-slate-200 p-6 rounded-lg"
                data-testid={`ticket-card-${ticket.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-mono text-slate-600 mb-1">
                      {ticket.ticket_number}
                    </p>
                    <h3 className="text-xl font-bold text-slate-900">
                      {ticket.subject}
                    </h3>
                  </div>
                  {getStatusBadge(ticket.status)}
                </div>
                <p className="text-slate-600 mb-3">{ticket.description}</p>
                {ticket.remarks && (
                  <div className="mt-4 p-4 bg-slate-50 border border-slate-200">
                    <p className="text-sm font-semibold text-slate-900 mb-1">
                      Admin Response:
                    </p>
                    <p className="text-sm text-slate-600">{ticket.remarks}</p>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    Created: {new Date(ticket.created_at).toLocaleString()}
                  </span>
                  <span>
                    Updated: {new Date(ticket.updated_at).toLocaleString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </ClientLayout>
  );
}
